import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  DropdownMenu,
  Icon,
  Progress,
  TextInput,
  Tooltip,
  type IconName,
  type MenuItem,
  TruncatedText,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import { DateField } from '../date-field';
import { FinanceTip, type FinanceTipText } from './finance/finance-tip';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The furniture both contract details are built from.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **C24 AND C25 BUILT THESE TWO SCREENS OUT OF THE PRODUCT'S GENERIC PARTS AND THEY CAME OUT
 * LOOKING LIKE SOMETHING ELSE.** Held against `2fazis.ContractDetail.html` and
 * `2fazis.LineItemDetail.html` the difference was not a number here or there: the mockup's details
 * are made of *uppercase 11px section strips with a count and a right-aligned action*, *bordered
 * flex rows rather than `<table>`s*, *`rounded-xl` rail cards with an 84px label column*, and *a
 * history that reads as sentences*. Ours were `<h3>`s, HTML tables, `DetailBox`es and a dump of
 * audit field names. Every piece was defensible on its own and the sum looked adjacent to the
 * product rather than part of it.
 *
 * So the shapes live here, once, named after what the mockup calls them — and both details are
 * assembled from them rather than each transcribing the same measurements again.
 *
 * ⚠️ **Every number is measured out of the two mockup files**, not chosen: `11px` uppercase
 * tracking-wide section labels, `84px` rail label columns, `30px` rail controls, `size-6` history
 * bubbles, `size-7` file tiles.
 */

/** `Sortételek · 2 db · [+ Új sortétel]` — the strip above every block on both details. */
export function SectionHeader({
  title,
  note,
  children,
}: {
  readonly title: string;
  /** The mockup's `itemNote` / `fileNote` / `historyNote` — a count, in mono. */
  readonly note?: string;
  /** The right-aligned action, if the section has one. */
  readonly children?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {title}
      </div>
      {note === undefined || note === '' ? null : (
        <span className="text-muted-foreground text-[11px] tabular-nums">{note}</span>
      )}
      <div className="flex-1" />
      {children}
    </div>
  );
}

/** The ghost button a section header's action is — `Új sortétel`, `Fájl csatolása`. */
export function SectionAction({
  icon = 'plus',
  label,
  onClick,
}: {
  readonly icon?: IconName;
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <Button variant="ghost" size="sm" className="text-muted-foreground gap-1.5" onClick={onClick}>
      <Icon name={icon} size={13} />
      {label}
    </Button>
  );
}

/**
 * A rail card. `bg-card rounded-xl border shadow-sm`, with the title in a bordered strip —
 * except the status card, which the mockup draws with the title inside its padding (`padded`).
 */
export function RailCard({
  title,
  note,
  padded = false,
  children,
}: {
  readonly title: string;
  /** Right-aligned in the header strip — the recurrence card's `Havonta · dec 31.-ig`. */
  readonly note?: string;
  readonly padded?: boolean;
  readonly children: ReactNode;
}) {
  if (padded) {
    return (
      <div className="bg-card rounded-xl border p-3 shadow-sm">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {title}
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border shadow-sm">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {title}
        </div>
        {note === undefined ? null : (
          <>
            <div className="flex-1" />
            <span className="text-muted-foreground text-[11px]">{note}</span>
          </>
        )}
      </div>
      <div className="flex flex-col gap-0.5 p-2">{children}</div>
    </div>
  );
}

/** One `84px | control` line inside a rail card. */
export function RailRow({
  label,
  divided = false,
  tip,
  children,
}: {
  readonly label: string;
  /** The mockup rules a line above the read-only counts at the bottom of each card. */
  readonly divided?: boolean;
  /**
   * ⚠️ **What the figure on this row MEANS — 14 Oct, the line item's Pénzügy card.** *"An info
   * button next to every value, like on the financial dashboard"*, so it is the dashboard's own
   * `FinanceTip` — the same `?`, the same name / meaning / formula / reading — rather than a second
   * way of explaining a number. Already translated by the caller.
   */
  readonly tip?: FinanceTipText;
  readonly children: ReactNode;
}) {
  const t = useT();
  return (
    <div
      className={`grid grid-cols-[84px_minmax(0,1fr)] items-center gap-2 ${divided ? 'border-t pt-2' : ''}`}
    >
      <span className="text-muted-foreground pl-1 text-[11.5px]">
        {tip === undefined ? (
          label
        ) : (
          <FinanceTip tip={tip} calcLabel={t('finance.calc')}>
            {label}
          </FinanceTip>
        )}
      </span>
      {children}
    </div>
  );
}

/**
 * A read-only rail value — the counts and dates that are not editable.
 *
 * ⚠️⚠️ **THE SAME BOX AS AN EDITABLE ROW SINCE ITEM 37, AND IT USED TO BE A DIFFERENT ONE.** This
 * was a bare `<span>` at `pl-2 text-[11.5px]` while `RailPicker`, `RailLink` and `RailDate` are all
 * `h-[30px] px-2 text-[12.5px]` — so a rail of eight rows had the read-only ones sitting a pixel
 * high, a pixel left and a point small, and the column of values visibly wandered. *"The field
 * sizes made uniform"* is that, and the fix is one box rather than four.
 *
 * ⚠️ **The MUTED colour stays**, because that is what still says *"you cannot change this"* once
 * the geometry no longer does. Losing both would make a read-only row look like an editable one
 * that has stopped responding.
 */
export function RailValue({
  children,
  mono = true,
}: {
  readonly children: ReactNode;
  readonly mono?: boolean;
}) {
  return (
    <span
      className={`text-muted-foreground flex h-[30px] min-w-0 items-center px-2 text-[12.5px] ${mono ? 'tabular-nums' : ''}`}
    >
      {/* 22 Oct 2026 · a long unbroken value ran past the rail — truncated, the rest in a tooltip. */}
      <TruncatedText>{children}</TruncatedText>
    </span>
  );
}

/**
 * The borderless dropdown a rail row's editable value is.
 *
 * ⚠️ **A `DropdownMenu`, not a `Select`** — and this is the one place in the product where that is
 * right. The mockup's rail controls carry a **colour dot or an avatar** beside the label, which a
 * native `<option>` cannot render, and the project and owner pickers are searchable. `select.tsx`'s
 * note stands everywhere else.
 */
export function RailPicker({
  label,
  value,
  items,
  disabled = false,
  searchPlaceholder,
  before,
}: {
  /** Names the menu for a screen reader. */
  readonly label: string;
  readonly value: string;
  readonly items: MenuItem[];
  readonly disabled?: boolean;
  readonly searchPlaceholder?: string;
  /** A dot or an avatar, drawn before the label. */
  readonly before?: ReactNode;
}) {
  if (disabled) {
    return (
      <span className="flex h-[30px] items-center gap-2 px-2 text-[12.5px]">
        {before}
        <span className="min-w-0 flex-1 truncate">{value}</span>
      </span>
    );
  }

  return (
    <DropdownMenu
      label={label}
      items={items}
      width={260}
      align="start"
      className="w-full"
      {...(searchPlaceholder === undefined ? {} : { searchPlaceholder })}
    >
      <button
        type="button"
        className="hover:bg-accent/60 flex h-[30px] w-full items-center gap-2 rounded-md px-2 text-left text-[12.5px] transition-colors"
      >
        {before}
        <span className="min-w-0 flex-1 truncate">{value}</span>
        <Icon name="chevron-down" size={12} className="text-muted-foreground opacity-60" />
      </button>
    </DropdownMenu>
  );
}

/** A rail row that navigates instead of editing — the line item's Projekt and Szerződés. */
export function RailLink({
  value,
  onClick,
  before,
  title,
}: {
  readonly value: string;
  readonly onClick: () => void;
  readonly before?: ReactNode;
  readonly title: string;
}) {
  /* ⚠️ The kit tooltip, never a native `title` (product owner's standing rule). */
  return (
    <Tooltip asChild content={title}>
      <button
        type="button"
        className="hover:bg-accent/60 flex h-[30px] w-full items-center gap-2 rounded-md px-2 text-left text-[12.5px] transition-colors"
        onClick={onClick}
      >
        {before}
        <span className="min-w-0 flex-1 truncate">{value}</span>
        <Icon name="arrow-right" size={12} className="text-muted-foreground opacity-60" />
      </button>
    </Tooltip>
  );
}

/**
 * A rail date, on the design system's own picker.
 *
 * ⚠️ **It was an `<input type="date">` and that was wrong twice over**: the mockup draws
 * `Shadcn.DatePicker` here, and a native date input renders in the BROWSER's locale — so a
 * Hungarian rail showed `mm/dd/yyyy`. `date-field.tsx` carries the full argument.
 */
export function RailDate({
  value,
  onChange,
  disabled = false,
  label,
  clearable = true,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly label: string;
  readonly clearable?: boolean;
}) {
  return (
    <DateField
      label={label}
      value={value}
      disabled={disabled}
      clearable={clearable}
      /* Borderless, like every other control in a rail row — the card supplies the chrome. */
      buttonClassName="h-[30px] border-transparent shadow-none"
      onChange={onChange}
    />
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A figure somebody types into a rail row — a budget, a revenue, a rate.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE MONEY CARD WAS READ-ONLY AND THE ENDPOINT BEHIND IT WAS NOT.** C55 built
 * `PUT /line-items/:id/money` and wired exactly one control to it — the scope picker — so a
 * deliverable's budget, revenue and rate could be read on the screen, summed on the dashboard and
 * set by nothing. Reported as *"it's like it's read only"*, which is what it was.
 *
 * ⚠️ **Commits on blur, not per keystroke.** A figure is typed a digit at a time and each digit is
 * a write, a version bump and an audit row; `Escape` reverts, which is the same contract the task
 * description follows.
 *
 * ⚠️ **Empty means *no figure*, not zero** — the endpoint's `null`. A deliverable nobody has
 * priced yet and one priced at nothing are different facts, and the card draws them differently.
 */
export function RailMoney({
  value,
  onCommit,
  disabled = false,
  label,
  suffix,
  placeholder = '–',
}: {
  /** The stored figure as text, or `''` when there is none. */
  readonly value: string;
  readonly onCommit: (value: number | null) => void;
  readonly disabled?: boolean;
  readonly label: string;
  /** The currency symbol, or the rate's unit. Rendered after the box, never inside it. */
  readonly suffix?: string;
  readonly placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  /* ⚠️ Re-seeded whenever the stored figure changes underneath — a live update while somebody is
     NOT typing must reach the box, and one while they are must not. */
  if (!editing && draft !== value) setDraft(value);

  const commit = (): void => {
    setEditing(false);
    const cleaned = draft.replace(/\s/g, '').replace(',', '.');
    if (cleaned === '') {
      if (value !== '') onCommit(null);
      return;
    }
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setDraft(value);
      return;
    }
    if (parsed !== Number(value)) onCommit(parsed);
  };

  return (
    <span className="flex h-[30px] items-center gap-1.5">
      <input
        value={draft}
        disabled={disabled}
        aria-label={label}
        placeholder={placeholder}
        inputMode="decimal"
        className="h-[30px] w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-right text-[12px] tabular-nums outline-none disabled:cursor-default hover:enabled:border-input focus:border-input focus:bg-background"
        onChange={(event) => {
          setEditing(true);
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            /* Handled — the modal around it stays open (`useModalSurface`). */
            event.preventDefault();
            setDraft(value);
            setEditing(false);
            event.currentTarget.blur();
          }
        }}
      />
      {suffix === undefined ? null : (
        <span className="text-muted-foreground shrink-0 pr-1 text-[11.5px]">{suffix}</span>
      )}
    </span>
  );
}

/** The status card's shared body: estimate versus tracked, a percentage, and a bar. */
export function ProgressBlock({
  estimate,
  tracked,
  pct,
  over,
  label,
}: {
  readonly estimate: string;
  readonly tracked: string;
  readonly pct: number;
  readonly over: boolean;
  readonly label: string;
}) {
  const t = useT();
  return (
    <>
      <div className="mt-3 flex items-center justify-between text-[11.5px]">
        <span className="text-muted-foreground">
          {t('contract.progress', { estimate, tracked })}
        </span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <Progress
        value={pct}
        label={label}
        className="mt-1.5"
        {...(over ? { color: 'var(--destructive)' } : {})}
      />
    </>
  );
}

/** The mono code chip in a detail's first line. */
export function CodeChip({
  code,
  icon,
  title,
  onClick,
}: {
  readonly code: string;
  readonly icon: IconName;
  readonly title: string;
  readonly onClick: () => void;
}) {
  return (
    <Tooltip asChild content={title}>
      <button
        type="button"
        className="text-muted-foreground hover:bg-accent hover:text-foreground flex h-6 items-center gap-1.5 rounded-md px-1.5 text-[12px] transition-colors"
        onClick={onClick}
      >
        {code}
        <Icon name={icon} size={12} />
      </button>
    </Tooltip>
  );
}

/** The `⋯` menu both details put at the end of their first line. */
export function MoreMenu({ items, label }: { readonly items: MenuItem[]; readonly label: string }) {
  return (
    <DropdownMenu items={items} width={240} align="end" label={label}>
      <Button variant="ghost" size="icon-sm" aria-label={label}>
        <Icon name="ellipsis" size={15} />
      </Button>
    </DropdownMenu>
  );
}

/**
 * The title, edited in place.
 *
 * ⚠️ **The TITLE ITSELF is the control**, which is the mockup's shape and was a pencil in ours. A
 * 19px heading you can click reads as editable once you have hovered it; a pencil beside it is a
 * second target for the same act.
 */
export function InlineTitle({
  value,
  canEdit,
  onSave,
}: {
  readonly value: string;
  readonly canEdit: boolean;
  readonly onSave: (next: string) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const save = (): void => {
    const next = draft.trim();
    if (next === '' || next === value) {
      setEditing(false);
      setDraft(value);
      return;
    }
    onSave(next);
    setEditing(false);
  };

  if (!editing) {
    if (!canEdit) return <span className="text-[19px] leading-snug font-semibold">{value}</span>;
    return (
      <Tooltip asChild content={t('contract.action.rename')}>
        <button
          type="button"
          className="hover:bg-accent/40 -mx-1.5 w-full rounded-md px-1.5 py-1 text-left"
          onClick={() => {
            setEditing(true);
          }}
        >
          <span className="text-[19px] leading-snug font-semibold">{value}</span>
        </button>
      </Tooltip>
    );
  }

  return (
    <div>
      <TextInput
        autoFocus
        value={draft}
        aria-label={t('contract.action.rename')}
        className="h-9 text-[16px] font-semibold"
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            save();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            setEditing(false);
            setDraft(value);
          }
        }}
      />
      <div className="mt-1.5 flex gap-2">
        <Button size="sm" onClick={save}>
          {t('common.save')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditing(false);
            setDraft(value);
          }}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}

/** The amber strip a closed contract carries. */
export function ClosedNotice({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
      <Icon name="triangle-alert" size={14} className="mt-0.5 text-amber-600" />
      <div className="text-[11.5px] leading-relaxed">{children}</div>
    </div>
  );
}

/**
 * The three-slot frame both details share.
 *
 * ⚠️ **The rail comes SECOND in the source and second visually on a narrow screen** — the mockup
 * places header/side/main by explicit grid coordinates so that the properties rail sits under the
 * title in a drawer and beside it on a page. Reordering with `order-*` on a flex column is the
 * narrow half of the same trick.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE HEADER STAYS, AND THE TWO COLUMNS SCROLL APART — 14 Oct.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Two sentences from the product owner: the code row and the title *"should be sticky, … always
 * visible when scrolling"*, and the rail *"should be scrollable separately … scrolling the main part
 * leaves the sidebar intact"*. Both were one scroll region with everything in it, so a long history
 * took the title away and dragged the status card off with it.
 *
 * ⚠️ **So the wide frame is a column that fills its surface**: the header on top, then a row whose
 * two halves each carry their own `overflow-y-auto`. Nothing here is fixed-height — it is
 * `min-h-0 flex-1` all the way up, so a modal still shrinks to a short record and a page takes the
 * shell's height. Each surface's body is a plain flex column for that reason, not a scroller.
 *
 * ⚠️ **The padding lives INSIDE each scroller**, so the scrollbar sits in the gutter between the
 * columns rather than against the text, and the 24px between them is the two halves' 12px each.
 *
 * ⚠️ **Below `lg` it is one region again** — a 900px window cannot hold a 308px rail beside a 360px
 * body — and the header is `sticky` instead. The drawer is the same: the `Sheet` owns the scroll,
 * and the header pins to its top. `sticky` is also what keeps the header when a host is NOT a
 * bounded flex column (an older page route that scrolls a block): the halves then grow to their
 * content, the host scrolls both, and the header still stays.
 *
 * ⚠️ **Opaque, with a rule under it**, because content passes beneath it.
 */
export function DetailFrame({
  wide,
  header,
  rail,
  main,
}: {
  readonly wide: boolean;
  readonly header: ReactNode;
  readonly rail: ReactNode;
  readonly main: ReactNode;
}) {
  if (!wide) {
    return (
      <div className="text-foreground flex flex-col">
        <div className="bg-background sticky top-0 z-10 flex min-w-0 flex-col gap-4 border-b px-5 pt-5 pb-4">
          {header}
        </div>
        <div className="flex flex-col gap-6 p-5">
          <div className="flex min-w-0 flex-col gap-3">{rail}</div>
          <div className="flex min-w-0 flex-col gap-5">{main}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-foreground flex min-h-0 flex-1 flex-col">
      <div className="bg-background sticky top-0 z-10 flex min-w-0 shrink-0 flex-col gap-4 border-b px-5 pt-5 pb-4">
        {header}
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5 lg:flex-row lg:gap-0 lg:overflow-visible lg:p-0">
        <div className="order-2 flex min-w-0 flex-col gap-5 lg:order-1 lg:min-w-[360px] lg:flex-1 lg:overflow-y-auto lg:py-5 lg:pr-3 lg:pl-5">
          {main}
        </div>
        <div className="order-1 flex min-w-0 flex-col gap-3 lg:order-2 lg:w-[308px] lg:shrink-0 lg:overflow-y-auto lg:py-5 lg:pr-5 lg:pl-3">
          {rail}
        </div>
      </div>
    </div>
  );
}
